/**
 * React Query hook for fetching challenges
 */

import { useQuery, useInfiniteQuery } from '@tanstack/react-query'
import { useApiClient } from '../../../helpers/apiClient.helper'
import {
  fetchActiveChallenges,
  fetchAllActiveChallenges,
  fetchChallengesPage,
  fetchGroupChallenges,
  fetchGroupChallengesPage,
} from '../helpers/challengesApi'
import { GROUP_CHALLENGE_HISTORY_PAGE_SIZE } from '../../friends/constants/groups.constants'
import {
  ARENA_STATUS_FILTER,
  ARENA_ACTIVE_STATUS,
  ARENA_ACTIVE_PAGE_SIZE,
  ARENA_ACTIVE_MAX_PAGES,
  ARENA_HISTORY_STATUS,
  ARENA_HISTORY_INITIAL_PAGE_SIZE,
  ARENA_HISTORY_NEXT_PAGE_SIZE,
  ARENA_HISTORY_PREVIEW_SIZE,
  GROUP_LEADERBOARD_CHALLENGES_LIMIT,
  GROUP_CHALLENGES_STATUS_JOINABLE,
  CHALLENGES_STALE_TIME_MS,
  CHALLENGES_GC_TIME_MS,
} from '../constants/challenges.constants'
import { challengesQueryKeys } from '../constants/challengesQueryKeys'
import type { ChallengeWithStats } from '../../../types/challenges'

/**
 * Hook to fetch arena challenges: PENDING, ACTIVE, and COMPLETED.
 * COMPLETED challenges appear in the Finished tab.
 */
export const useChallengesQuery = (
  userId: string | null,
  enabled: boolean = true
) => {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: challengesQueryKeys.list(userId),
    queryFn: async (): Promise<ChallengeWithStats[]> => {
      if (!userId) {
        return []
      }

      return fetchActiveChallenges(userId, apiClient, ARENA_STATUS_FILTER)
    },
    enabled: enabled && !!userId,
    staleTime: CHALLENGES_STALE_TIME_MS,
    gcTime: CHALLENGES_GC_TIME_MS,
    retry: 2,
  })
}

/**
 * Fetches challenges with higher limit for group leaderboard so older group challenges are included.
 */
export const useGroupChallengesQuery = (
  userId: string | null,
  enabled: boolean = true
) => {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: challengesQueryKeys.groupLeaderboard(userId),
    queryFn: async (): Promise<ChallengeWithStats[]> => {
      if (!userId) return []
      return fetchActiveChallenges(
        userId,
        apiClient,
        ARENA_STATUS_FILTER,
        GROUP_LEADERBOARD_CHALLENGES_LIMIT
      )
    },
    enabled: enabled && !!userId,
    staleTime: CHALLENGES_STALE_TIME_MS,
    gcTime: CHALLENGES_GC_TIME_MS,
    retry: 2,
  })
}

/**
 * Fetches all completed challenges for a group (full group history).
 * Used on the group leaderboard screen so every member sees the same table.
 */
export const useGroupChallengesForLeaderboardQuery = (
  groupId: string | null,
  enabled: boolean = true
) => {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: challengesQueryKeys.groupCompleted(groupId),
    queryFn: async (): Promise<ChallengeWithStats[]> => {
      if (!groupId) return []
      return fetchGroupChallenges(groupId, apiClient)
    },
    enabled: enabled && !!groupId,
    staleTime: CHALLENGES_STALE_TIME_MS,
    gcTime: CHALLENGES_GC_TIME_MS,
    retry: 2,
  })
}

/**
 * Infinite query for completed group challenges (group leaderboard history).
 * Initial page 40, then 40 per page on scroll. Use with FlashList onEndReached.
 */
export const useGroupChallengesInfiniteQuery = (
  groupId: string | null,
  enabled: boolean = true
) => {
  const apiClient = useApiClient()

  return useInfiniteQuery({
    queryKey: challengesQueryKeys.groupCompletedInfinite(groupId),
    queryFn: async ({ pageParam = 0 }) => {
      if (!groupId) return { data: [], hasMore: false }
      return fetchGroupChallengesPage(
        groupId,
        apiClient,
        GROUP_CHALLENGE_HISTORY_PAGE_SIZE,
        pageParam,
        'COMPLETED'
      )
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined
      const offset = allPages.reduce((acc, p) => acc + p.data.length, 0)
      return offset
    },
    initialPageParam: 0,
    enabled: enabled && !!groupId,
    staleTime: CHALLENGES_STALE_TIME_MS,
    gcTime: CHALLENGES_GC_TIME_MS,
    retry: 2,
  })
}

/**
 * Fetches joinable challenges for a group (PENDING/ACTIVE where current user is participant).
 */
export const useGroupJoinableChallengesQuery = (
  groupId: string | null,
  enabled: boolean = true
) => {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: challengesQueryKeys.groupJoinable(groupId),
    queryFn: async (): Promise<ChallengeWithStats[]> => {
      if (!groupId) return []
      return fetchGroupChallenges(groupId, apiClient, GROUP_CHALLENGES_STATUS_JOINABLE)
    },
    enabled: enabled && !!groupId,
    staleTime: CHALLENGES_STALE_TIME_MS,
    gcTime: CHALLENGES_GC_TIME_MS,
    retry: 2,
  })
}

/**
 * Fetches every PENDING + ACTIVE challenge for the Arena by walking pages until
 * the server reports `hasMore: false` (capped at ARENA_ACTIVE_MAX_PAGES pages
 * × ARENA_ACTIVE_PAGE_SIZE rows).
 */
export const useActiveChallengesQuery = (
  userId: string | null,
  enabled: boolean = true
) => {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: challengesQueryKeys.active(userId),
    queryFn: async (): Promise<ChallengeWithStats[]> => {
      if (!userId) return []
      return fetchAllActiveChallenges(
        apiClient,
        ARENA_ACTIVE_STATUS,
        ARENA_ACTIVE_PAGE_SIZE,
        ARENA_ACTIVE_MAX_PAGES,
      )
    },
    enabled: enabled && !!userId,
    staleTime: CHALLENGES_STALE_TIME_MS,
    gcTime: CHALLENGES_GC_TIME_MS,
    retry: 2,
  })
}

/**
 * Fetches the 5 most recent completed challenges for the preview strip in the All tab.
 * Fires in parallel with the active query on page load — no user interaction required.
 */
export const useChallengesHistoryPreviewQuery = (
  userId: string | null,
  enabled: boolean = true
) => {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: challengesQueryKeys.historyPreview(userId),
    queryFn: async (): Promise<ChallengeWithStats[]> => {
      if (!userId) return []
      const result = await fetchChallengesPage(
        apiClient,
        ARENA_HISTORY_STATUS,
        ARENA_HISTORY_PREVIEW_SIZE,
        0
      )
      return result.data
    },
    enabled: enabled && !!userId,
    staleTime: CHALLENGES_STALE_TIME_MS,
    gcTime: CHALLENGES_GC_TIME_MS,
    retry: 2,
  })
}

/**
 * Paginated history (COMPLETED). First page uses ARENA_HISTORY_INITIAL_PAGE_SIZE,
 * subsequent pages use ARENA_HISTORY_NEXT_PAGE_SIZE (infinite scroll).
 */
export const useChallengesHistoryInfiniteQuery = (
  userId: string | null,
  enabled: boolean = true
) => {
  const apiClient = useApiClient()

  return useInfiniteQuery({
    queryKey: challengesQueryKeys.history(userId),
    queryFn: async ({ pageParam = 0 }) => {
      const isFirstPage = pageParam === 0
      const limit = isFirstPage
        ? ARENA_HISTORY_INITIAL_PAGE_SIZE
        : ARENA_HISTORY_NEXT_PAGE_SIZE
      return fetchChallengesPage(
        apiClient,
        ARENA_HISTORY_STATUS,
        limit,
        pageParam
      )
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) return undefined
      const offset = allPages.reduce((acc, p) => acc + p.data.length, 0)
      return offset
    },
    initialPageParam: 0,
    enabled: enabled && !!userId,
    staleTime: CHALLENGES_STALE_TIME_MS,
    gcTime: CHALLENGES_GC_TIME_MS,
    retry: 2,
  })
}
